/** @format */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableHead,
    TableRow,
    MenuItem,
    Select,
    CircularProgress,
    Chip,
    Stack,
    FormControl,
    InputLabel,
    Tooltip,
} from "@mui/material";
import {
    Refresh,
    CheckCircleOutline,
    HighlightOff,
    CancelOutlined,
    OpenInNew,
    HandshakeOutlined,
} from "@mui/icons-material";

import { AppDispatch } from "../../redux/store/store";
import {
    fetchBusinessPromises,
    markPromiseKept,
    markPromiseBroken,
    cancelInvoicePromise,
} from "../../redux/actions/collectAction";
import {
    selectBusinessPromises,
    selectIsLoadingBusinessPromises,
    selectBusinessPromisesError,
} from "../../redux/selectors/collectSelector";
import {
    IPromiseToPay,
    PromiseStatus,
} from "../../utility/types/collectTypes";
import { useToast } from "../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectSectionCard,
    CollectSectionHeader,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectTableContainer,
    CollectTableHeadCell,
    CollectTableBodyCell,
    CollectEmptyState,
    CollectEmptyTitle,
    CollectEmptyHint,
    CollectPrimaryButton,
    CollectGhostButton,
    CollectKpiCard,
    CollectKpiLabel,
    CollectKpiValue,
    CollectKpiHint,
    FlexBetween,
} from "./styles";

// ─────────────────────────────────────────────────────────────────────────
// CollectPromises (Stage 2B) — workspace-wide promise-to-pay register.
// Lists every promise a customer has made across all invoices, with at-a-
// glance KPIs and inline kept / broken / cancel actions for pending ones.
// Pending promises pause dunning; the watchdog Lambda auto-breaks any that
// drift past their grace period.
// ─────────────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<PromiseStatus, string> = {
    pending: "#f59e0b",
    kept: "#16a34a",
    broken: "#dc2626",
    cancelled: "#94a3b8",
};

const STATUS_LABELS: Record<PromiseStatus, string> = {
    pending: "Pending",
    kept: "Kept",
    broken: "Broken",
    cancelled: "Cancelled",
};

function fmtCurrency(n?: number, currency = "USD"): string {
    if (n === undefined || n === null || isNaN(n)) return "—";
    try {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(n);
    } catch {
        return `$${n.toFixed(0)}`;
    }
}

function fmtDate(v?: string): string {
    if (!v) return "—";
    const d = new Date(v);
    if (isNaN(d.getTime())) return "—";
    try {
        return d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch {
        return v;
    }
}

/** True when a still-pending promise is already past its promised date. */
function isPastDue(p: IPromiseToPay): boolean {
    if (p.status !== "pending" || !p.promisedDate) return false;
    const due = new Date(p.promisedDate);
    if (isNaN(due.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return due.getTime() < today.getTime();
}

function customerLabel(p: IPromiseToPay): string {
    return (
        p.customer?.displayName ||
        p.invoice?.customerDisplayName ||
        "—"
    );
}

function invoiceLabel(p: IPromiseToPay): string {
    const doc = p.invoice?.docNumber;
    return doc ? `#${doc}` : "View invoice";
}

const FILTERS: Array<{ value: "all" | PromiseStatus; label: string }> = [
    { value: "all", label: "All promises" },
    { value: "pending", label: "Pending" },
    { value: "kept", label: "Kept" },
    { value: "broken", label: "Broken" },
    { value: "cancelled", label: "Cancelled" },
];

const CollectPromises: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const dispatch = useDispatch<AppDispatch>();

    const promises = useSelector(selectBusinessPromises);
    const loading = useSelector(selectIsLoadingBusinessPromises);
    const error = useSelector(selectBusinessPromisesError);

    const [statusFilter, setStatusFilter] = useState<"all" | PromiseStatus>(
        "all",
    );
    const [pendingId, setPendingId] = useState<string | null>(null);

    const load = useCallback(() => {
        // Pull a wide page (max 200) so the KPI cards reflect the full book
        // of promises and the client-side status filter stays accurate.
        dispatch(fetchBusinessPromises({ limit: 200 }));
    }, [dispatch]);

    useEffect(() => {
        load();
    }, [load]);

    const summary = useMemo(() => {
        let pending = 0;
        let kept = 0;
        let broken = 0;
        let cancelled = 0;
        let pendingAmount = 0;
        let pastDue = 0;
        for (const p of promises) {
            if (p.status === "pending") {
                pending += 1;
                pendingAmount += p.promisedAmount || 0;
                if (isPastDue(p)) pastDue += 1;
            } else if (p.status === "kept") kept += 1;
            else if (p.status === "broken") broken += 1;
            else if (p.status === "cancelled") cancelled += 1;
        }
        return { pending, kept, broken, cancelled, pendingAmount, pastDue };
    }, [promises]);

    const currency = promises[0]?.currency || "USD";

    const visible = useMemo(() => {
        const filtered =
            statusFilter === "all"
                ? promises
                : promises.filter((p) => p.status === statusFilter);
        // Pending first, then by promised date ascending (soonest due first).
        return [...filtered].sort((a, b) => {
            if (a.status === "pending" && b.status !== "pending") return -1;
            if (b.status === "pending" && a.status !== "pending") return 1;
            const da = new Date(a.promisedDate || 0).getTime();
            const db = new Date(b.promisedDate || 0).getTime();
            return da - db;
        });
    }, [promises, statusFilter]);

    const resolve = async (
        promiseId: string,
        kind: "kept" | "broken" | "cancel",
    ) => {
        const action =
            kind === "kept"
                ? markPromiseKept
                : kind === "broken"
                    ? markPromiseBroken
                    : cancelInvoicePromise;
        setPendingId(promiseId);
        try {
            const result = await dispatch(action({ promiseId }));
            if ((result as any).meta?.requestStatus === "fulfilled") {
                toast.success(
                    kind === "kept"
                        ? "Promise marked as kept."
                        : kind === "broken"
                            ? "Promise marked as broken."
                            : "Promise cancelled.",
                );
                // The mutation reducers update invoice-scoped state only, so
                // re-pull the business register to reflect the new status.
                load();
            } else {
                toast.error(
                    (result as any).payload || "Couldn't update promise.",
                );
            }
        } finally {
            setPendingId(null);
        }
    };

    return (
        <CollectPageWrapper>
            <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
                <FlexBetween sx={{ mb: 2 }}>
                    <Box>
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                fontWeight: 800,
                                color: "#0f172a",
                            }}
                        >
                            Promises to Pay
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "'Montserrat', sans-serif",
                                color: "#64748b",
                                fontSize: "0.9rem",
                            }}
                        >
                            Every payment commitment customers have made. Pending
                            promises pause reminders until their promised date.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <CollectGhostButton
                            startIcon={<Refresh fontSize="small" />}
                            onClick={load}
                            disabled={loading}
                        >
                            Refresh
                        </CollectGhostButton>
                    </Stack>
                </FlexBetween>

                {/* KPIs */}
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr 1fr",
                            sm: "repeat(5, 1fr)",
                        },
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <CollectKpiCard accent={STATUS_COLORS.pending}>
                        <CollectKpiLabel>Pending</CollectKpiLabel>
                        <CollectKpiValue>{summary.pending}</CollectKpiValue>
                        <CollectKpiHint>
                            {summary.pastDue > 0
                                ? `${summary.pastDue} past due`
                                : "awaiting payment"}
                        </CollectKpiHint>
                    </CollectKpiCard>
                    <CollectKpiCard accent={STATUS_COLORS.pending}>
                        <CollectKpiLabel>Pending value</CollectKpiLabel>
                        <CollectKpiValue>
                            {fmtCurrency(summary.pendingAmount, currency)}
                        </CollectKpiValue>
                        <CollectKpiHint>promised, not yet paid</CollectKpiHint>
                    </CollectKpiCard>
                    <CollectKpiCard accent={STATUS_COLORS.kept}>
                        <CollectKpiLabel>Kept</CollectKpiLabel>
                        <CollectKpiValue>{summary.kept}</CollectKpiValue>
                        <CollectKpiHint>honoured on time</CollectKpiHint>
                    </CollectKpiCard>
                    <CollectKpiCard accent={STATUS_COLORS.broken}>
                        <CollectKpiLabel>Broken</CollectKpiLabel>
                        <CollectKpiValue>{summary.broken}</CollectKpiValue>
                        <CollectKpiHint>missed the date</CollectKpiHint>
                    </CollectKpiCard>
                    <CollectKpiCard accent={STATUS_COLORS.cancelled}>
                        <CollectKpiLabel>Cancelled</CollectKpiLabel>
                        <CollectKpiValue>{summary.cancelled}</CollectKpiValue>
                        <CollectKpiHint>withdrawn</CollectKpiHint>
                    </CollectKpiCard>
                </Box>

                <CollectSectionCard>
                    <CollectSectionHeader>
                        <Box>
                            <CollectSectionTitle>
                                {visible.length > 0
                                    ? `${visible.length} promise${visible.length === 1 ? "" : "s"}`
                                    : "Promises"}
                            </CollectSectionTitle>
                            <CollectSectionSubtitle>
                                Pending shown first, soonest promised date next
                            </CollectSectionSubtitle>
                        </Box>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                label="Status"
                                value={statusFilter}
                                onChange={(e) =>
                                    setStatusFilter(
                                        e.target.value as "all" | PromiseStatus,
                                    )
                                }
                            >
                                {FILTERS.map((f) => (
                                    <MenuItem key={f.value} value={f.value}>
                                        {f.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </CollectSectionHeader>

                    {loading && promises.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: "center" }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : error ? (
                        <CollectEmptyState>
                            <CollectEmptyTitle>
                                Couldn&apos;t load promises
                            </CollectEmptyTitle>
                            <CollectEmptyHint>{error}</CollectEmptyHint>
                        </CollectEmptyState>
                    ) : visible.length === 0 ? (
                        <CollectEmptyState>
                            <HandshakeOutlined
                                sx={{ fontSize: 40, color: "#cbd5e1", mb: 1 }}
                            />
                            <CollectEmptyTitle>
                                No promises here yet
                            </CollectEmptyTitle>
                            <CollectEmptyHint>
                                When a customer commits to a payment date, from a
                                reply or recorded manually on an invoice, it shows
                                up here.
                            </CollectEmptyHint>
                        </CollectEmptyState>
                    ) : (
                        <CollectTableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <CollectTableHeadCell>
                                            Invoice
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Customer
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Promised
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Promised date
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Status
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell>
                                            Created
                                        </CollectTableHeadCell>
                                        <CollectTableHeadCell align="right">
                                            Actions
                                        </CollectTableHeadCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visible.map((p) => {
                                        const pastDue = isPastDue(p);
                                        const isPending = p.status === "pending";
                                        const rowPending = pendingId === p._id;
                                        return (
                                            <TableRow key={p._id} hover>
                                                <CollectTableBodyCell>
                                                    <Box
                                                        component="span"
                                                        onClick={() =>
                                                            p.invoiceId &&
                                                            navigate(
                                                                `/collect/invoices/${p.invoiceId}`,
                                                            )
                                                        }
                                                        sx={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            gap: 0.5,
                                                            color: "#143AA2",
                                                            fontWeight: 600,
                                                            cursor: p.invoiceId
                                                                ? "pointer"
                                                                : "default",
                                                        }}
                                                    >
                                                        {invoiceLabel(p)}
                                                        {p.invoiceId && (
                                                            <OpenInNew
                                                                sx={{
                                                                    fontSize: 14,
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {customerLabel(p)}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell align="right">
                                                    {fmtCurrency(
                                                        p.promisedAmount,
                                                        p.currency || currency,
                                                    )}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection:
                                                                "column",
                                                        }}
                                                    >
                                                        <span>
                                                            {fmtDate(
                                                                p.promisedDate,
                                                            )}
                                                        </span>
                                                        {pastDue && (
                                                            <Typography
                                                                component="span"
                                                                sx={{
                                                                    fontFamily:
                                                                        "'Montserrat', sans-serif",
                                                                    fontSize:
                                                                        "0.7rem",
                                                                    fontWeight: 700,
                                                                    color: "#dc2626",
                                                                }}
                                                            >
                                                                Past due
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            STATUS_LABELS[
                                                            p.status
                                                            ]
                                                        }
                                                        sx={{
                                                            backgroundColor: `${STATUS_COLORS[p.status]}1a`,
                                                            color: STATUS_COLORS[
                                                                p.status
                                                            ],
                                                            fontWeight: 700,
                                                            fontFamily:
                                                                "'Montserrat', sans-serif",
                                                            fontSize: "0.7rem",
                                                        }}
                                                    />
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell>
                                                    {fmtDate(p.createdAt)}
                                                </CollectTableBodyCell>
                                                <CollectTableBodyCell align="right">
                                                    {isPending ? (
                                                        <Stack
                                                            direction="row"
                                                            spacing={0.5}
                                                            justifyContent="flex-end"
                                                        >
                                                            <Tooltip title="Mark kept">
                                                                <span>
                                                                    <CollectGhostButton
                                                                        size="small"
                                                                        disabled={
                                                                            rowPending
                                                                        }
                                                                        onClick={() =>
                                                                            resolve(
                                                                                p._id,
                                                                                "kept",
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            minWidth:
                                                                                0,
                                                                            px: 1,
                                                                            color: "#16a34a",
                                                                        }}
                                                                    >
                                                                        <CheckCircleOutline fontSize="small" />
                                                                    </CollectGhostButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Mark broken">
                                                                <span>
                                                                    <CollectGhostButton
                                                                        size="small"
                                                                        disabled={
                                                                            rowPending
                                                                        }
                                                                        onClick={() =>
                                                                            resolve(
                                                                                p._id,
                                                                                "broken",
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            minWidth:
                                                                                0,
                                                                            px: 1,
                                                                            color: "#dc2626",
                                                                        }}
                                                                    >
                                                                        <HighlightOff fontSize="small" />
                                                                    </CollectGhostButton>
                                                                </span>
                                                            </Tooltip>
                                                            <Tooltip title="Cancel promise">
                                                                <span>
                                                                    <CollectGhostButton
                                                                        size="small"
                                                                        disabled={
                                                                            rowPending
                                                                        }
                                                                        onClick={() =>
                                                                            resolve(
                                                                                p._id,
                                                                                "cancel",
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            minWidth:
                                                                                0,
                                                                            px: 1,
                                                                            color: "#94a3b8",
                                                                        }}
                                                                    >
                                                                        <CancelOutlined fontSize="small" />
                                                                    </CollectGhostButton>
                                                                </span>
                                                            </Tooltip>
                                                        </Stack>
                                                    ) : (
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                fontFamily:
                                                                    "'Montserrat', sans-serif",
                                                                fontSize:
                                                                    "0.75rem",
                                                                color: "#94a3b8",
                                                            }}
                                                        >
                                                            {p.resolutionReason ||
                                                                "—"}
                                                        </Typography>
                                                    )}
                                                </CollectTableBodyCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </CollectTableContainer>
                    )}
                </CollectSectionCard>
            </Box>
        </CollectPageWrapper>
    );
};

export default CollectPromises;
